import React, { Component } from 'react';
import { IconButton, Popper, Card } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { size, truncate, get, debounce } from 'lodash';
import Autosuggest from 'react-autosuggest';
import PropTypes from 'prop-types';

import TooltipWrapper from '../TooltipWrapper';
import TextField from '../../TextField/TextField';
import MenuItem from '../../MenuItem/MenuItem';
import SelectFieldListItemTip from './SelectFieldListItemTip';
import ListItemTip from './ListItemTip';

export function ChargerTip({ text = '', hintText = '', maxTextSize = 25 }) {
  const textSize = size(text);
  const truncatedText = truncate(text, { length: maxTextSize });
  const tipDiv = <div className="chargers-autocomplete-tooltip">{hintText || text}</div>;

  return textSize > maxTextSize ? (
    <TooltipWrapper position="top-end" tip={tipDiv}>
      {truncatedText}
    </TooltipWrapper>
  ) : (
    <div className="root-nodes-item">
      <div className="root-nodes-item-border">{text}</div>
    </div>
  );
}

export const chargerAutocompleteStyles = {
  popper: {
    zIndex: 1000,
  },
  itemStyle: {
    borderBottom: '1px solid rgb(240, 240, 240)',
    height: '50px',
    display: 'flex',
    flexDirection: 'column-reverse',
    alignItems: 'flex-start',
    maxWidth: '100%',
  },
  itemStyleOnlyLabel: {
    borderBottom: '1px solid rgb(240, 240, 240)',
    height: '15px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
    maxWidth: '100%',
  },
  itemId: {
    color: 'gray',
    fontSize: '9px',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
  container: {
    width: '100%',
    height: '32px',
  },
  suggestionsContainerOpen: {
    position: 'absolute',
    zIndex: 2,
    left: 0,
    right: 0,
  },
  suggestion: {
    display: 'block',
  },
  suggestionsList: {
    margin: 0,
    padding: 0,
    listStyleType: 'none',
  },
};

const containerStyle = {
  position: 'relative',
  width: '100%',
  marginTop: '0px!important',
};

const MAX_CUT_VALUE_LENGTH = 26;

class RenderChargerDropdownAutocomplete extends Component {
  state = {
    value: '',
    dataSource: [],
    suggestion: null,
    isInputFocused: false,
  };

  handleInputFocus = () => this.setState({ isInputFocused: true });

  handleInputBlur = () => this.setState({ isInputFocused: false });

  anchorEl = null;

  getData = debounce((suggestion) => {
    const query = get(suggestion, 'value', '');

    this.props.getData(query).then((resp) => {
      this.setState({ ...resp });
    });
  }, 400);

  onChangeCallback = (event, { suggestion }) => {
    const {
      input: { onChange, value },
      updateName,
    } = this.props;

    if (suggestion === null) {
      onChange(null);
    } else if (value !== suggestion.value) {
      this.setState({ suggestion });
      onChange(suggestion.value);
      if (updateName) {
        updateName({ value: suggestion.label });
      }
    }
  };

  onChangeNullCallback = () => {
    const {
      input: { onChange },
      updateName,
    } = this.props;

    onChange(null);
    this.setState({ suggestion: null, value: '' });
    if (updateName) {
      updateName({ value: null, label: null });
    }
  };

  componentDidMount() {
    this.getData();
  }

  componentDidUpdate(prevProps, prevState) {
    const { customerId: currentCustomerId } = this.props;
    const { customerId: prevCustomerId } = prevProps;

    if (currentCustomerId && prevCustomerId && currentCustomerId !== prevCustomerId) {
      this.onChangeNullCallback();
      this.props.getData('').then((resp) => {
        this.setState({ ...resp, isInputFocused: false });
      });
    }
  }

  renderInputComponent = (inputProps) => {
    const { classes, inputRef = () => {}, onBlur, onFocus, ref, ...other } = inputProps;
    const inputPropsValue = inputProps.value;
    const {
      input: { value },
      hideTooltipOnShortTitle,
    } = this.props;

    const error = this.props.meta.submitFailed && this.props.meta.error;

    const textfield = (
      <TextField
        {...other}
        error={error}
        helperText={error}
        required={this.props.required}
        disabled={this.props.disabled || this.props.readOnly}
        fullWidth
        InputProps={{
          onBlur,
          onFocus,
          inputRef: (node) => {
            ref(node);
            inputRef(node);
          },
        }}
      />
    );

    return inputPropsValue !== '' && value && (!hideTooltipOnShortTitle || inputPropsValue.length > MAX_CUT_VALUE_LENGTH) ? (
      <ListItemTip text={inputPropsValue} maxTextSize="23" hideHighlight hintText={inputPropsValue || 'empty'} child={textfield} />
    ) : (
      textfield
    );
  };

  handleChange = (event, { newValue }) => {
    this.setState({ value: newValue });
  };

  onSuggestionsClearRequested = () => {
    this.setState({
      dataSource: [],
    });
  };

  getItemElement = (item, { isHighlighted }) => (
    <MenuItem
      key={item.value}
      selected={isHighlighted}
      style={!this.props.showOnlyLabel ? chargerAutocompleteStyles.itemStyle : chargerAutocompleteStyles.itemStyleOnlyLabel}
      component="div"
    >
      <SelectFieldListItemTip
        key={item.value}
        value={item.label}
        label={item.value}
        itemId={item.value || 'empty'}
        itemName={item.label}
        maxTextSize="26"
      >
        <div className="charger-list-item-id faults-filter-cut" style={chargerAutocompleteStyles.itemLabel}>
          {item.label}
        </div>
        {!this.props.showOnlyLabel && (
          <div className="charger-list-item-name faults-filter-cut" style={chargerAutocompleteStyles.itemId}>
            {item.value}
          </div>
        )}
      </SelectFieldListItemTip>
    </MenuItem>
  );

  shouldRenderSuggestions = () => true;

  getSuggestionValue = (suggestion) => suggestion.value;

  setAnchorEl = (node) => {
    this.anchorEl = node;
  };

  render() {
    const {
      input: { value, name },
      floatingLabelText,
      readOnly,
      disabled,
    } = this.props;

    const isDisabled = this.props.disabled || this.props.readOnly;
    const options = this.state.dataSource
      .sort((a, b) => (a.value.toLowerCase() > b.value.toLowerCase() ? 1 : b.value.toLowerCase() < a.value.toLowerCase() ? -1 : 0))
      .filter((item) => item.value !== value);

    const wrapperClassName = `autocompleteWrapper${disabled ? ' disabled' : ''}${value ? ' selected' : ''}`;

    const inputProps = {
      placeholder: isDisabled ? '' : 'Start typing Name or ID',
      label: floatingLabelText,
      value: value || this.state.value,
      onChange: this.handleChange,
      InputLabelProps: {
        shrink: true,
      },
      onFocus: this.handleInputFocus,
      onBlur: this.handleInputBlur,
      inputRef: (node) => {
        this.setAnchorEl(node);
      },
    };

    const cardStyle = {
      maxHeight: '300px',
      overflowY: 'auto',
      width: this.anchorEl ? this.anchorEl.clientWidth : undefined,
    };

    return (
      <div>
        <div className={wrapperClassName}>
          <Autosuggest
            name={name}
            key={name}
            renderInputComponent={this.renderInputComponent}
            suggestions={options}
            onSuggestionsFetchRequested={this.getData}
            onSuggestionsClearRequested={this.onSuggestionsClearRequested}
            getSuggestionValue={this.getSuggestionValue}
            renderSuggestion={this.getItemElement}
            shouldRenderSuggestions={this.shouldRenderSuggestions}
            onSuggestionSelected={this.onChangeCallback}
            theme={{
              container: containerStyle,
              suggestionsContainerOpen: chargerAutocompleteStyles.suggestionsContainerOpen,
              suggestionsList: chargerAutocompleteStyles.suggestionsList,
              suggestion: chargerAutocompleteStyles.suggestion,
            }}
            renderSuggestionsContainer={(options) =>
              this.anchorEl &&
              this.anchorEl.clientWidth > 0 && (
                <Popper
                  style={chargerAutocompleteStyles.popper}
                  anchorEl={this.anchorEl}
                  open={Boolean(options.children && this.state.isInputFocused)}
                >
                  <Card square {...options.containerProps} style={cardStyle}>
                    {options.children}
                  </Card>
                </Popper>
              )
            }
            inputProps={inputProps}
          />
          {value && !readOnly ? (
            <IconButton
              size="large"
              className="resetOwnerButton"
              style={chargerAutocompleteStyles.resetButton}
              onClick={this.onChangeNullCallback}
            >
              <CloseIcon />
            </IconButton>
          ) : null}
        </div>
      </div>
    );
  }
}

RenderChargerDropdownAutocomplete.propTypes = {
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  updateName: PropTypes.func,
  getData: PropTypes.func,
  required: PropTypes.bool,
  meta: PropTypes.object,
  floatingLabelText: PropTypes.string,
  input: PropTypes.object,
  hideTooltipOnShortTitle: PropTypes.bool,
  showOnlyLabel: PropTypes.bool,
};

export default RenderChargerDropdownAutocomplete;
